/**
 * 消費一覧の状態と操作（プロダクト仕様書「消費一覧」）。
 *
 * 指定した月・絞り込み・並べ替えは URL のクエリに持ち、消費詳細から戻ったときに、移る前のままにする。
 * クエリにお気に入りの絞り込みがないとき（サイドバーから開いたとき）は、お気に入りを1つ以上登録している利用者には、
 * お気に入りの企業だけに絞り込んで表示する。お気に入りは契約一覧と同じものを使う。
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { useAuthHeader } from '@/hooks/useAuth';
import * as api from '@/lib/api/credit-consumption';
import type { CompanyRow, ConsumptionList } from '@/lib/api/credit-consumption';
import { addFavorite, removeFavorite, toFailure } from '@/lib/api/credit-contracts';
import { isMonth } from '@/lib/credit-contracts/month';
import { trimWhiteSpace } from '@/lib/credit-conversion-settings/number';

import { fetchFailureMessage } from './display';
import type { SortDir, SortState } from './sorting';

export type Load<T> =
  | { status: 'loading' }
  | { status: 'ok'; data: T }
  | { status: 'error'; message: string };

/** 列のまとまり（企業の列を除く）。見出しの最上段に名前を示す */
export type Group = 'contract' | 'history' | 'snapshots' | 'breakdown';

export const groupLabels: Record<Group, string> = {
  contract: '契約との対比',
  history: '過去6か月の消費クレジット',
  snapshots: '時点値とデータコネクト',
  breakdown: '消費の内訳',
};

/** 初めの並べ替え：企業IDの順 */
export const DEFAULT_SORT: SortState = { key: 'id', dir: 'asc' };

/** 企業名か企業IDの一部で絞り込む。大文字と小文字は区別しない */
export function matches(company: CompanyRow, query: string): boolean {
  const needle = trimWhiteSpace(query).toLowerCase();
  if (!needle) return true;
  return company.name.toLowerCase().includes(needle) || company.id.toLowerCase().includes(needle);
}

interface UrlState {
  month: string | null;
  query: string;
  favoritesOnly: boolean;
  sort: SortState;
}

function readParams(params: URLSearchParams): UrlState & { favoritesGiven: boolean } {
  const month = params.get('month');
  const fav = params.get('favorites');
  const dir = params.get('dir');
  return {
    month: month && isMonth(month) ? month : null,
    query: params.get('q') ?? '',
    favoritesOnly: fav === '1',
    favoritesGiven: fav !== null,
    sort: {
      key: params.get('sort') || DEFAULT_SORT.key,
      dir: dir === 'desc' ? 'desc' : ('asc' as SortDir),
    },
  };
}

export function useConsumptionList() {
  const { getAuthHeader } = useAuthHeader();
  const [searchParams, setSearchParams] = useSearchParams();
  // 取得の途中で関数が作り直されても、走っている取得を続けられるよう参照で持つ
  const authRef = useRef(getAuthHeader);
  authRef.current = getAuthHeader;
  const setParamsRef = useRef(setSearchParams);
  setParamsRef.current = setSearchParams;
  // 画面が最後に書いた URL のクエリ。これと違うクエリになったら、画面の外から URL が変わった
  const lastSearch = useRef<string | null>(null);

  const [list, setList] = useState<Load<ConsumptionList>>({ status: 'loading' });
  // 表示している値があるときの取得中。取得し終えるまで前の値の表を残す
  const [refreshing, setRefreshing] = useState(false);
  const [view, setView] = useState<UrlState>({
    month: null,
    query: '',
    favoritesOnly: false,
    sort: DEFAULT_SORT,
  });
  const [toggling, setToggling] = useState<ReadonlySet<string>>(new Set());
  const sequence = useRef(0);
  // 取得とお気に入りの登録・解除の前後を比べる時計。取得を始めた後に登録・解除し終えた企業は、取得の結果より登録・解除を優先する
  const clock = useRef(0);
  const favoriteWrites = useRef(new Map<string, { favorite: boolean; at: number }>());
  const state = useRef<UrlState>(view);
  const favoritesUndecided = useRef(false);

  const writeParams = () => {
    const { month, query, favoritesOnly, sort } = state.current;
    const params = new URLSearchParams();
    if (month) params.set('month', month);
    if (query) params.set('q', query);
    params.set('favorites', favoritesOnly ? '1' : '0');
    if (sort.key !== DEFAULT_SORT.key || sort.dir !== DEFAULT_SORT.dir) {
      params.set('sort', sort.key);
      params.set('dir', sort.dir);
    }
    lastSearch.current = params.toString();
    setParamsRef.current(params, { replace: true });
  };

  const update = (patch: Partial<UrlState>) => {
    state.current = { ...state.current, ...patch };
    setView(state.current);
    writeParams();
  };

  /** 指定した月の値を取得する。お気に入りの絞り込みが決まっていなければ、取得した結果から決める */
  const fetchMonth = useCallback(async (month: string | null) => {
    const id = ++sequence.current;
    const startedAt = ++clock.current;
    setList((current) => (current.status === 'ok' ? current : { status: 'loading' }));
    setRefreshing(true);
    try {
      const fetched = await api.getConsumptionList(month, authRef.current());
      if (id !== sequence.current) return;
      const data = {
        ...fetched,
        companies: fetched.companies.map((c) => {
          const write = favoriteWrites.current.get(c.id);
          return write && write.at > startedAt ? { ...c, favorite: write.favorite } : c;
        }),
      };
      // 指定できる範囲の外の月は、サーバーが範囲の端の月にして返す
      state.current = { ...state.current, month: data.month };
      if (favoritesUndecided.current) {
        favoritesUndecided.current = false;
        state.current.favoritesOnly = data.companies.some((c) => c.favorite);
      }
      setView(state.current);
      setList({ status: 'ok', data });
      setRefreshing(false);
      writeParams();
    } catch (e) {
      if (id !== sequence.current) return;
      setList({ status: 'error', message: fetchFailureMessage(toFailure(e)) });
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    const current = searchParams.toString();
    if (current === lastSearch.current) return;
    lastSearch.current = current;
    const { favoritesGiven, ...read } = readParams(searchParams);
    state.current = read;
    favoritesUndecided.current = !favoritesGiven;
    setView(read);
    void fetchMonth(read.month);
  }, [searchParams, fetchMonth]);

  const selectMonth = (month: string) => {
    state.current = { ...state.current, month };
    void fetchMonth(month);
  };

  const reload = () => void fetchMonth(state.current.month);

  const setQuery = (query: string) => update({ query });

  const setFavoritesOnly = (favoritesOnly: boolean) => {
    favoritesUndecided.current = false;
    update({ favoritesOnly });
  };

  const setSort = (sort: SortState) => update({ sort });

  const toggleFavorite = async (company: CompanyRow) => {
    setToggling((s) => new Set(s).add(company.id));
    const next = !company.favorite;
    try {
      if (next) await addFavorite(company.id, authRef.current());
      else await removeFavorite(company.id, authRef.current());
      favoriteWrites.current.set(company.id, { favorite: next, at: ++clock.current });
      setList((current) =>
        current.status === 'ok'
          ? {
              ...current,
              data: {
                ...current.data,
                companies: current.data.companies.map((c) =>
                  c.id === company.id ? { ...c, favorite: next } : c,
                ),
              },
            }
          : current,
      );
    } catch (e) {
      const reason = toFailure(e).message.replace(/。$/, '');
      toast.error(
        `${company.name}を、お気に入り${next ? 'に登録' : 'から外すことが'}できませんでした（${reason}）。`,
      );
    } finally {
      setToggling((s) => {
        const rest = new Set(s);
        rest.delete(company.id);
        return rest;
      });
    }
  };

  const filtered = useMemo(
    () =>
      list.status === 'ok'
        ? list.data.companies.filter(
            (c) => (!view.favoritesOnly || c.favorite) && matches(c, view.query),
          )
        : [],
    [list, view.favoritesOnly, view.query],
  );

  return {
    list,
    refreshing: refreshing && list.status === 'ok',
    filtered,
    query: view.query,
    favoritesOnly: view.favoritesOnly,
    sort: view.sort,
    toggling,
    selectMonth,
    reload,
    setQuery,
    setFavoritesOnly,
    setSort,
    toggleFavorite,
  };
}
